import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import MoveraCareChatbot from "./components/MoveraCareChatbot";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Partners from "./pages/Partners";
import BookRide from "./pages/BookRide";
import Careers from "./pages/Careers";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import HIPAANotice from "./pages/HIPAANotice";
import AdminDashboard from "./pages/AdminDashboard";
import DispatchLogin from "./pages/DispatchLogin";
import DispatchForgotPassword from "./pages/DispatchForgotPassword";
import DispatchResetPassword from "./pages/DispatchResetPassword";
import DispatchIndex from "./pages/DispatchIndex";
import DispatchDashboard from "./pages/DispatchDashboard";
import DispatchBoard from "./pages/DispatchBoard";
import DispatchBookings from "./pages/DispatchBookings";
import DispatchBookingDetail from "./pages/DispatchBookingDetail";
import DispatchBookingTripSheet from "./pages/DispatchBookingTripSheet";
import DispatchInquiries from "./pages/DispatchInquiries";
import DispatchInquiryDetail from "./pages/DispatchInquiryDetail";
import DriverManagement from "./pages/DriverManagement";
import CalendarView from "./pages/CalendarView";

import LocationService from "./pages/LocationService";
import HoustonMedicalTransportation from "./pages/HoustonMedicalTransportation";
import SugarLandMedicalTransportation from "./pages/SugarLandMedicalTransportation";
import KatyMedicalTransportation from "./pages/KatyMedicalTransportation";
import RichmondMedicalTransportation from "./pages/RichmondMedicalTransportation";
import MissouriCityMedicalTransportation from "./pages/MissouriCityMedicalTransportation";
import NotFound from "./pages/NotFound";
import FAQPage from "./pages/FAQPage";
import HospitalDischarge from "./pages/HospitalDischarge";
import HealthcarePartners from "./pages/HealthcarePartners";
import Procurement from "./pages/Procurement";
import RequestPatientTransportation from "./pages/RequestPatientTransportation";
import WorkersCompensation from "./pages/WorkersCompensation";
import HealthcareFacilities from "./pages/partners/HealthcareFacilities";
import InsuranceTPA from "./pages/partners/InsuranceTPA";
import NEMTBrokers from "./pages/partners/NEMTBrokers";
import DialysisCenters from "./pages/partners/DialysisCenters";
import HospitalDischargePartners from "./pages/partners/HospitalDischargePartners";
import CaseManagers from "./pages/partners/CaseManagers";
import ReferralPartners from "./pages/partners/ReferralPartners";
import WheelchairTransportation from "./pages/services/WheelchairTransportation";
import AmbulatoryTransportation from "./pages/services/AmbulatoryTransportation";
import DialysisTransportation from "./pages/services/DialysisTransportation";
import TherapyRehabTransportation from "./pages/services/TherapyRehabTransportation";
import RecurringMedicalTransportation from "./pages/services/RecurringMedicalTransportation";
import { initAnalytics, trackPageView, attachGlobalClickTracking } from "./lib/analytics";

function Layout() {
  const location = useLocation();
  const isDispatchRoute = location.pathname.startsWith('/dispatch');
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isPrivateRoute = isDispatchRoute || isAdminRoute;

  // GA4 — no-op unless REACT_APP_GA4_MEASUREMENT_ID is set to a real G-XXXXXXXX id.
  // Attaches click tracking once; page-view fires on every route change (public routes only).
  React.useEffect(() => {
    const enabled = initAnalytics();
    if (enabled) attachGlobalClickTracking();
  }, []);
  React.useEffect(() => {
    if (isPrivateRoute) return; // never send private surface paths to GA
    trackPageView(location.pathname, document.title);
  }, [location.pathname, isPrivateRoute]);

  // Defense-in-depth: emit robots="noindex, nofollow" on every private route
  // regardless of individual page-level SEO tags. robots.txt is a crawl
  // instruction, not a hard block, so this belt-and-suspenders approach keeps
  // authenticated surfaces out of search indexes even if a crawler ignores it.
  //
  // Private routes must contain exactly ONE robots meta with noindex. Any
  // <meta name="robots"> from a previously-mounted public page's <Seo /> is
  // stripped before our tag is inserted so the prerendered HTML never carries
  // conflicting `index, follow` and `noindex` directives simultaneously.
  React.useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const ATTR = 'data-managed-by-private-route-guard';
    // Cleanup any previous tag we added.
    document
      .querySelectorAll(`meta[${ATTR}], link[${ATTR}]`)
      .forEach((el) => el.remove());
    if (!isPrivateRoute) return;
    // Purge ALL robots meta tags currently in <head> (including any left
    // behind by <Seo /> on a public route). Private routes must never expose
    // conflicting directives.
    document
      .querySelectorAll('meta[name="robots"], meta[name="Robots"], meta[name="ROBOTS"]')
      .forEach((el) => el.remove());
    const robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex, nofollow, noarchive');
    robots.setAttribute(ATTR, 'true');
    document.head.appendChild(robots);
    // Also mark canonical as self-referencing to avoid stray canonicals leaking
    // in from earlier public-page navigations.
    const canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', `${window.location.origin}${location.pathname}`);
    canonical.setAttribute(ATTR, 'true');
    document.head.appendChild(canonical);
  }, [isPrivateRoute, location.pathname]);

  return (
    <>
      {!isDispatchRoute && <Header />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/hospital-discharge" element={<HospitalDischarge />} />
        <Route path="/services/wheelchair-transportation" element={<WheelchairTransportation />} />
        <Route path="/services/ambulatory-transportation" element={<AmbulatoryTransportation />} />
        <Route path="/services/dialysis-transportation" element={<DialysisTransportation />} />
        <Route path="/services/therapy-rehabilitation-transportation" element={<TherapyRehabTransportation />} />
        <Route path="/services/recurring-medical-transportation" element={<RecurringMedicalTransportation />} />
        <Route path="/healthcare-partners" element={<HealthcarePartners />} />
        <Route path="/healthcare-partners/workers-compensation" element={<WorkersCompensation />} />
        <Route path="/healthcare-partners/healthcare-facilities" element={<HealthcareFacilities />} />
        <Route path="/healthcare-partners/insurance-tpa" element={<InsuranceTPA />} />
        <Route path="/healthcare-partners/nemt-brokers" element={<NEMTBrokers />} />
        <Route path="/healthcare-partners/dialysis-centers" element={<DialysisCenters />} />
        <Route path="/healthcare-partners/hospital-discharge" element={<HospitalDischargePartners />} />
        <Route path="/healthcare-partners/case-managers" element={<CaseManagers />} />
        <Route path="/healthcare-partners/referral-partners" element={<ReferralPartners />} />
        <Route path="/procurement" element={<Procurement />} />
        <Route path="/request-patient-transportation" element={<RequestPatientTransportation />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/book" element={<BookRide />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/hipaa" element={<HIPAANotice />} />
        <Route path="/admin" element={<AdminDashboard />} />
        
        {/* Location-Specific Pages for SEO */}
        <Route path="/houston-tx-medical-transportation" element={<HoustonMedicalTransportation />} />
        <Route path="/richmond-tx-medical-transportation" element={<RichmondMedicalTransportation />} />
        <Route path="/sugar-land-tx-medical-transportation" element={<SugarLandMedicalTransportation />} />
        <Route path="/katy-tx-medical-transportation" element={<KatyMedicalTransportation />} />
        <Route path="/missouri-city-tx-medical-transportation" element={<MissouriCityMedicalTransportation />} />
        
        <Route path="/dispatch" element={<DispatchIndex />} />
        <Route path="/dispatch/login" element={<DispatchLogin />} />
        <Route path="/dispatch/forgot-password" element={<DispatchForgotPassword />} />
        <Route path="/dispatch/reset-password" element={<DispatchResetPassword />} />
        <Route path="/dispatch/dashboard" element={<DispatchDashboard />} />
        <Route path="/dispatch/board" element={<DispatchBoard />} />
        <Route path="/dispatch/bookings" element={<DispatchBookings />} />
        <Route path="/dispatch/bookings/:id" element={<DispatchBookingDetail />} />
        <Route path="/dispatch/bookings/:id/trip-sheet" element={<DispatchBookingTripSheet />} />
        <Route path="/dispatch/inquiries/:kind" element={<DispatchInquiries />} />
        <Route path="/dispatch/inquiries/:kind/:id" element={<DispatchInquiryDetail />} />
        <Route path="/dispatch/drivers" element={<DriverManagement />} />
        <Route path="/dispatch/calendar" element={<CalendarView />} />

        {/* 404 catch-all — must remain last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isDispatchRoute && <Footer />}
      {!isPrivateRoute && <MoveraCareChatbot />}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Layout />
      </BrowserRouter>
    </div>
  );
}

export default App;
